import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { Loader2, Megaphone, Users, CheckCircle2, AlertTriangle, ChevronLeft, Send } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

export function WhatsAppBroadcastDialog({ open, onOpenChange }) {
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState({}); // key: phone -> {phone, customer_id, name}
  const [mode, setMode] = useState('text');
  const [text, setText] = useState('');
  const [templateName, setTemplateName] = useState('');
  const [templateLang, setTemplateLang] = useState('en_US');
  const [templateParams, setTemplateParams] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [signature, setSignature] = useState(true);
  const [step, setStep] = useState('compose'); // compose | confirm | result
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) return;
    setStep('compose'); setResult(null); setSelected({});
    setMode('text'); setText(''); setTemplateName(''); setTemplateParams([]);
    setStatusFilter('all'); setSearch(''); setSignature(true);
    (async () => {
      setLoading(true);
      try {
        const r = await api.get('/whatsapp/recipients');
        setCustomers(r.data.customers || []);
        setContacts(r.data.contacts || []);
      } catch {
        toast.error('Could not load recipients');
      } finally { setLoading(false); }
      try {
        const t = await api.get('/whatsapp/templates');
        setTemplates(t.data.templates || []);
      } catch {
        setTemplates([]);
      }
    })();
  }, [open]);

  const statuses = useMemo(() => {
    const s = new Set(customers.map((c) => c.status).filter(Boolean));
    return ['all', ...Array.from(s).sort()];
  }, [customers]);

  const filteredCustomers = useMemo(() => {
    const q = search.trim().toLowerCase();
    return customers.filter((c) =>
      (statusFilter === 'all' || c.status === statusFilter) &&
      (!q || c.name.toLowerCase().includes(q) || (c.phone || '').includes(q)));
  }, [customers, statusFilter, search]);

  const filteredContacts = useMemo(() => {
    const q = search.trim().toLowerCase();
    return contacts.filter((c) => !q || (c.name || '').toLowerCase().includes(q) || (c.phone || '').includes(q));
  }, [contacts, search]);

  const selectedList = Object.values(selected);
  const toggle = (r) => setSelected((prev) => {
    const next = { ...prev };
    if (next[r.phone]) delete next[r.phone]; else next[r.phone] = r;
    return next;
  });
  const selectAll = (list) => setSelected((prev) => {
    const next = { ...prev };
    list.forEach((c) => { next[c.phone] = { phone: c.phone, customer_id: c.customer_id || '', name: c.name }; });
    return next;
  });

  const selectedTpl = templates.find((t) => t.name === templateName);
  const tplVarCount = selectedTpl?.param_count || 0;

  const doSend = async () => {
    setSending(true);
    try {
      const payload = {
        mode,
        text: mode === 'text' ? text.trim() : '',
        template_name: mode === 'template' ? templateName : '',
        language_code: templateLang,
        body_values: mode === 'template' ? templateParams : [],
        signature,
        recipients: selectedList,
      };
      const { data } = await api.post('/whatsapp/broadcast', payload);
      setResult(data);
      setStep('result');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Broadcast failed');
    } finally { setSending(false); }
  };

  const canProceed = selectedList.length > 0 &&
    ((mode === 'text' && text.trim()) || (mode === 'template' && templateName));

  const RecipientRow = ({ c }) => (
    <label className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-50 cursor-pointer">
      <Checkbox checked={!!selected[c.phone]}
        onCheckedChange={() => toggle({ phone: c.phone, customer_id: c.customer_id || '', name: c.name })}
        data-testid={`broadcast-recipient-${c.phone}`} />
      <div className="min-w-0 flex-1">
        <p className="text-sm truncate">{c.name}</p>
        <p className="text-xs text-slate-400">{c.phone}{c.status ? ` · ${c.status}` : ''}</p>
      </div>
    </label>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col" data-testid="wa-broadcast-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Megaphone className="w-5 h-5 text-emerald-600" /> Broadcast WhatsApp message
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" /></div>
        ) : step === 'result' ? (
          <div className="space-y-4 overflow-y-auto">
            <div className="flex items-center gap-2 text-emerald-700">
              <CheckCircle2 className="w-5 h-5" />
              <span className="font-semibold">Sent to {result.sent_count} · Skipped {result.skipped_count}</span>
            </div>
            {result.skipped_count > 0 && (
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
                <p className="text-sm font-medium text-amber-800 flex items-center gap-1.5 mb-1">
                  <AlertTriangle className="w-4 h-4" /> Skipped recipients
                </p>
                <ScrollArea className="max-h-40">
                  {result.skipped.map((s, i) => (
                    <p key={i} className="text-xs text-amber-800">{s.name || s.phone} — {s.reason}</p>
                  ))}
                </ScrollArea>
              </div>
            )}
            <DialogFooter>
              <Button onClick={() => onOpenChange(false)} data-testid="broadcast-done-btn">Done</Button>
            </DialogFooter>
          </div>
        ) : step === 'confirm' ? (
          <div className="space-y-4">
            <div className="rounded-md bg-slate-50 border p-4 text-center">
              <Users className="w-6 h-6 mx-auto text-emerald-600 mb-1" />
              <p className="text-lg font-semibold">This will message {selectedList.length} {selectedList.length === 1 ? 'person' : 'people'}</p>
              <p className="text-sm text-slate-500 mt-1">Each gets a private 1:1 chat — they won't see each other.</p>
              {mode === 'text' && (
                <p className="text-xs text-amber-600 mt-2">Free-text is skipped for anyone outside their 24-hour window.</p>
              )}
            </div>
            <div className="rounded-md border p-3">
              <p className="text-xs text-slate-400 mb-1">Message</p>
              <p className="text-sm whitespace-pre-wrap">{mode === 'text' ? text : `[Template: ${templateName}]${templateParams.length ? ' · ' + templateParams.join(' · ') : ''}`}</p>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setStep('compose')} data-testid="broadcast-back-btn">
                <ChevronLeft className="w-4 h-4 mr-1" /> Back
              </Button>
              <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={doSend} disabled={sending} data-testid="broadcast-send-btn">
                {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Send to {selectedList.length}
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4 overflow-hidden flex-1">
            {/* Recipients */}
            <div className="flex flex-col min-h-0 border rounded-md">
              <div className="p-2 border-b space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Recipients</span>
                  <Badge variant="secondary" data-testid="broadcast-selected-count">{selectedList.length} selected</Badge>
                </div>
                <Input placeholder="Search name or number..." value={search} onChange={(e) => setSearch(e.target.value)} className="h-8 text-sm" data-testid="broadcast-search" />
              </div>
              <Tabs defaultValue="customers" className="flex-1 flex flex-col min-h-0">
                <TabsList className="mx-2 mt-2">
                  <TabsTrigger value="customers" data-testid="broadcast-tab-customers">Customers</TabsTrigger>
                  <TabsTrigger value="contacts" data-testid="broadcast-tab-contacts">Contacts</TabsTrigger>
                </TabsList>
                <TabsContent value="customers" className="flex-1 min-h-0 flex flex-col m-0">
                  <div className="flex items-center gap-2 px-2 py-1.5">
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                      <SelectTrigger className="h-8 text-xs flex-1" data-testid="broadcast-status-filter"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {statuses.map((s) => <SelectItem key={s} value={s}>{s === 'all' ? 'All statuses' : s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => selectAll(filteredCustomers)} data-testid="broadcast-select-all-customers">
                      Select all ({filteredCustomers.length})
                    </Button>
                  </div>
                  <ScrollArea className="flex-1 px-1">
                    {filteredCustomers.map((c) => <RecipientRow key={c.phone} c={c} />)}
                    {filteredCustomers.length === 0 && <p className="text-xs text-slate-400 p-4 text-center">No customers match</p>}
                  </ScrollArea>
                </TabsContent>
                <TabsContent value="contacts" className="flex-1 min-h-0 flex flex-col m-0">
                  <div className="px-2 py-1.5">
                    <Button variant="outline" size="sm" className="h-8 text-xs w-full" onClick={() => selectAll(filteredContacts)} data-testid="broadcast-select-all-contacts">
                      Select all ({filteredContacts.length})
                    </Button>
                  </div>
                  <ScrollArea className="flex-1 px-1">
                    {filteredContacts.map((c) => <RecipientRow key={c.phone} c={c} />)}
                    {filteredContacts.length === 0 && <p className="text-xs text-slate-400 p-4 text-center">No contacts match</p>}
                  </ScrollArea>
                </TabsContent>
              </Tabs>
            </div>

            {/* Message */}
            <div className="flex flex-col min-h-0 space-y-2 overflow-y-auto">
              <Tabs value={mode} onValueChange={setMode}>
                <TabsList className="w-full">
                  <TabsTrigger value="text" className="flex-1" data-testid="broadcast-mode-text">Free text</TabsTrigger>
                  <TabsTrigger value="template" className="flex-1" data-testid="broadcast-mode-template">Template</TabsTrigger>
                </TabsList>
              </Tabs>
              {mode === 'text' ? (
                <>
                  <Textarea placeholder="Type your message..." value={text} onChange={(e) => setText(e.target.value)}
                    className="min-h-[160px] text-sm" data-testid="broadcast-text" />
                  <p className="text-xs text-amber-600">Only delivers to people who messaged you in the last 24h; others are skipped and reported.</p>
                </>
              ) : (
                <>
                  <Select value={templateName} onValueChange={(v) => { setTemplateName(v); const t = templates.find((x) => x.name === v); setTemplateLang(t?.language || 'en_US'); setTemplateParams(Array((t?.param_count) || 0).fill('')); }}>
                    <SelectTrigger data-testid="broadcast-template-select"><SelectValue placeholder="Choose approved template" /></SelectTrigger>
                    <SelectContent>
                      {templates.map((t) => <SelectItem key={t.name} value={t.name}>{t.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {selectedTpl?.body_text && <p className="text-xs text-slate-500 bg-slate-50 rounded p-2 whitespace-pre-wrap">{selectedTpl.body_text}</p>}
                  {Array.from({ length: tplVarCount }).map((_, i) => (
                    <Input key={i} placeholder={`Value for {{${i + 1}}}`} value={templateParams[i] || ''}
                      onChange={(e) => setTemplateParams((p) => { const n = [...p]; n[i] = e.target.value; return n; })}
                      className="h-8 text-sm" data-testid={`broadcast-tpl-param-${i}`} />
                  ))}
                  <p className="text-xs text-emerald-600">Templates reach everyone, inside or outside the 24h window.</p>
                </>
              )}
              <label className="flex items-center gap-2 text-xs text-slate-500 cursor-pointer" data-testid="broadcast-signature-toggle">
                <Checkbox checked={signature} onCheckedChange={(v) => setSignature(!!v)} data-testid="broadcast-signature-checkbox" />
                Add signature (your name + “Helios Electrical and Solar”)
              </label>
              <div className="mt-auto pt-2">
                <Button className="w-full bg-emerald-600 hover:bg-emerald-700" disabled={!canProceed}
                  onClick={() => setStep('confirm')} data-testid="broadcast-review-btn">
                  Review · {selectedList.length} recipient{selectedList.length === 1 ? '' : 's'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
